import type {Germline} from 'antibodies/germline/useGermlinesQuery';
import {AntibodyDomainType, GermlineSupportedChain} from 'graphql-client/__generated/graphqlGlobalTypes';

type GermlineDomainSupport = Pick<Germline, 'supportedChains' | 'constantDomains'>;

const GERMLINE_EXEMPT_DOMAIN_TYPES: ReadonlyArray<AntibodyDomainType> = [
  AntibodyDomainType.L,
  AntibodyDomainType.F,
  AntibodyDomainType.ELEMENT,
];

/** A variable domain is supported if the germline supports any of these chains. */
const CHAINS_BY_VARIABLE_DOMAIN_TYPE: Partial<
  Record<AntibodyDomainType, ReadonlyArray<GermlineSupportedChain>>
> = {
  [AntibodyDomainType.VH]: [GermlineSupportedChain.HEAVY],
  [AntibodyDomainType.VHH]: [GermlineSupportedChain.HEAVY],
  [AntibodyDomainType.VL]: [GermlineSupportedChain.KAPPA, GermlineSupportedChain.LAMBDA],
  [AntibodyDomainType.VA]: [GermlineSupportedChain.ALPHA],
  [AntibodyDomainType.VB]: [GermlineSupportedChain.BETA],
  [AntibodyDomainType.VG]: [GermlineSupportedChain.GAMMA],
  [AntibodyDomainType.VD]: [GermlineSupportedChain.DELTA],
};

const SINGLE_LIGHT_CHAIN_WARNINGS: Partial<Record<GermlineSupportedChain, string>> = {
  [GermlineSupportedChain.KAPPA]:
    'This germline only supports kappa light chains; lambda sequences will not be annotated.',
  [GermlineSupportedChain.LAMBDA]:
    'This germline only supports lambda light chains; kappa sequences will not be annotated.',
};

/** Germlines without PipeBio metadata are treated as supporting every domain. */
function isDomainTypeSupportedByGermline(
  germline: GermlineDomainSupport,
  domainType: AntibodyDomainType
): boolean {
  if (GERMLINE_EXEMPT_DOMAIN_TYPES.includes(domainType)) {
    return true;
  }
  const {supportedChains, constantDomains} = germline;
  const chains = CHAINS_BY_VARIABLE_DOMAIN_TYPE[domainType];
  if (chains != null) {
    return supportedChains == null || chains.some((chain) => supportedChains.includes(chain));
  }
  return constantDomains == null || constantDomains.includes(domainType);
}

export function isGermlineCompatibleWithDomainTypes(
  germline: GermlineDomainSupport,
  domainTypes: ReadonlyArray<AntibodyDomainType>
): boolean {
  return domainTypes.every((domainType) => isDomainTypeSupportedByGermline(germline, domainType));
}

/** Warns when the domains include a VL but the germline supports only one of kappa or lambda. */
export function getLightChainWarning(
  germline: GermlineDomainSupport,
  domainTypes: ReadonlyArray<AntibodyDomainType>
): string | null {
  if (!domainTypes.includes(AntibodyDomainType.VL) || germline.supportedChains == null) {
    return null;
  }
  const lightChains = germline.supportedChains.filter(
    (chain) => chain === GermlineSupportedChain.KAPPA || chain === GermlineSupportedChain.LAMBDA
  );
  if (lightChains.length !== 1) {
    return null;
  }
  return SINGLE_LIGHT_CHAIN_WARNINGS[lightChains[0]] ?? null;
}
